import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../api/api";
import { formatDateDDMMYYYY } from "../utils/dateFormat";
import "./ScrapEntry.css";

const MONTH_ABBRS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const calculateMonth = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${year}_${mm}_${MONTH_ABBRS[d.getMonth()]}`;
};

const toISODate = (dateStr) => (dateStr ? new Date(dateStr).toISOString() : null);

const EMPTY_FORM = {
  Product_type: null,
  Moved_from: null,
  Boe_no: null,
  Inv_no: "",
  Inv_date: "",
  Month: "",
  Qty: "",
  Net_wt: "",
  Scrap_qty: "",
  Tr6_challan_no: "",
  Tr6_challan_date: "",
  Duty_paid: "",
  Quantity_in_kgs: "",
  Value: "",
  Remaining_igst_available: "",
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "26px",
    fontSize: "12px",
    borderColor: state.isFocused ? "#7a9cc3" : "#b8c1ce",
    boxShadow: "none",
    borderRadius: 0,
    backgroundColor: "#fffef3",
    "&:hover": { borderColor: "#7a9cc3" },
  }),
  menu: (base) => ({
    ...base,
    fontSize: "12px",
    zIndex: 30,
    borderRadius: 0,
    overflow: "hidden",
    boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
    border: "1px solid #b8c1ce",
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isSelected ? "#4d78ad" : isFocused ? "#e8f1fb" : "white",
    color: isSelected ? "white" : "#1f1f1f",
    padding: "6px 8px",
    cursor: "pointer",
  }),
  singleValue: (base) => ({ ...base, color: "#1f1f1f" }),
  placeholder: (base) => ({ ...base, color: "#7a7a7a" }),
};

const Label = ({ children, required }) => (
  <label className="scrap-field-label">
    {children}
    {required && <span className="scrap-field-required">*</span>}
  </label>
);

const TextInput = ({ value, onChange, placeholder, readOnly, className = "" }) => (
  <input
    type="text"
    value={value}
    onChange={onChange}
    readOnly={readOnly}
    placeholder={placeholder}
    className={`scrap-input ${readOnly ? "scrap-readonly" : ""} ${className}`}
  />
);

const NumberInput = ({ value, onChange, placeholder }) => (
  <input
    type="number"
    step="any"
    value={value}
    onChange={onChange}
    onWheel={(e) => e.target.blur()}
    placeholder={placeholder ?? "0.00"}
    className="scrap-number"
  />
);

const DateInput = ({ value, onChange }) => (
  <input
    type="date"
    value={value}
    onChange={onChange}
    className="scrap-date"
  />
);

const formatCurrentDateTime = () =>
  new Date().toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

const ScrapEntry = () => {
  const navigate = useNavigate();
  const [view, setView] = useState("list");
  const [dropdowns, setDropdowns] = useState({ productTypes: [], movedFroms: [], boeNos: [] });
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [loadingInit, setLoadingInit] = useState(true);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [editLoading, setEditLoading] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [currentDateTime, setCurrentDateTime] = useState(formatCurrentDateTime());

  useEffect(() => {
    const init = async () => {
      setLoadingInit(true);
      const userName = sessionStorage.getItem("userName");
      try {
        const [ddRes, listRes] = await Promise.all([
          api.GetScrapDetailsBoE(userName),
          api.GetAllScrapDetailsBoE(userName),
        ]);
        setDropdowns({
          productTypes: (ddRes?.Table || []).map((r) => ({ value: r.ProductType, label: r.ProductType })),
          movedFroms: (ddRes?.Table1 || []).map((r) => ({ value: r.Moved_from, label: r.Moved_from })),
          boeNos: (ddRes?.Table2 || []).map((r) => ({ value: r.Rev_BOE, label: r.Rev_BOE })),
        });
        setRecords(Array.isArray(listRes) ? listRes : listRes?.Table || []);
      } catch (err) {
        console.error("Init error:", err);
        toast.error("Failed to load page data.");
      } finally {
        setLoadingInit(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(formatCurrentDateTime());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const refreshRecords = useCallback(async () => {
    setLoadingRecords(true);
    try {
      const userName = sessionStorage.getItem("userName");
      const res = await api.GetAllScrapDetailsBoE(userName);
      setRecords(Array.isArray(res) ? res : res?.Table || []);
    } catch (err) {
      console.error("Refresh error:", err);
    } finally {
      setLoadingRecords(false);
    }
  }, []);

  const handleFieldChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "Inv_date") next.Month = calculateMonth(value);
      return next;
    });
  };

  const handleNew = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setSelectedRow(null);
    setView("form");
  };

  const handleEdit = async (primeId) => {
    setEditLoading(primeId);
    try {
      const res = await api.EditScrapBoE(primeId);
      const rec = Array.isArray(res) ? res[0] : res?.Table?.[0] ?? res;
      if (!rec) {
        toast.error("Record not found.");
        return;
      }

      const toDateInput = (iso) => (iso ? iso.substring(0, 10) : "");
      const invDateStr = toDateInput(rec.Inv_date);

      setForm({
        Product_type: rec.Product_type ? { value: rec.Product_type, label: rec.Product_type } : null,
        Moved_from: rec.Moved_from ? { value: rec.Moved_from, label: rec.Moved_from } : null,
        Boe_no: rec.Boe_no ? { value: rec.Boe_no, label: rec.Boe_no } : null,
        Inv_no: rec.Inv_no ?? "",
        Inv_date: invDateStr,
        Month: rec.Month ?? calculateMonth(invDateStr),
        Qty: rec.Qty ?? "",
        Net_wt: rec.Net_wt ?? "",
        Scrap_qty: rec.Scrap_qty ?? "",
        Tr6_challan_no: rec.Tr6_challan_no ?? "",
        Tr6_challan_date: toDateInput(rec.Tr6_challan_date),
        Duty_paid: rec.Duty_paid ?? "",
        Quantity_in_kgs: rec.Quantity_in_kgs ?? "",
        Value: rec.Value ?? "",
        Remaining_igst_available: rec.Remaining_igst_available ?? "",
      });
      setEditId(primeId);
      setView("form");
    } catch (err) {
      console.error("Edit error:", err);
      toast.error("Failed to load record for editing.");
    } finally {
      setEditLoading(null);
    }
  };

  const handleSave = async () => {
    if (!form.Product_type || !form.Moved_from || !form.Inv_no || !form.Inv_date) {
      toast.warn("Please fill in all required fields.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        SqlAction: editId ? "UPDATE" : "INSERT",
        ...(editId ? { Id: editId } : {}),
        Product_type: form.Product_type?.value ?? "",
        Moved_from: form.Moved_from?.value ?? "",
        Inv_no: form.Inv_no,
        Inv_date: toISODate(form.Inv_date),
        Month: form.Month,
        Qty: parseFloat(form.Qty) || 0,
        Net_wt: parseFloat(form.Net_wt) || 0,
        Boe_no: form.Boe_no?.value ?? null,
        Scrap_qty: parseFloat(form.Scrap_qty) || null,
        Tr6_challan_no: form.Tr6_challan_no || null,
        Tr6_challan_date: form.Tr6_challan_date ? toISODate(form.Tr6_challan_date) : null,
        Duty_paid: parseFloat(form.Duty_paid) || null,
        Quantity_in_kgs: parseFloat(form.Quantity_in_kgs) || null,
        Value: parseFloat(form.Value) || null,
        Remaining_igst_available: parseFloat(form.Remaining_igst_available) || null,
      };

      const res = await api.SaveScrapBoE(payload);
      const status = res?.[0]?.Status ?? res?.Status ?? 1;
      const message = res?.[0]?.Message ?? res?.Message ?? "Saved successfully!";

      if (status === 1 || status === "1") {
        toast.success(message || "Saved successfully!");
        setForm(EMPTY_FORM);
        setEditId(null);
        setView("list");
        await refreshRecords();
      } else {
        toast.error(message || "Save failed. Please try again.");
      }
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Error saving record. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setView("list");
  };

  const filteredRecords = records.filter((row) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return Object.values(row).some(
      (v) => v !== null && v !== undefined && String(v).toLowerCase().includes(q)
    );
  });

  if (loadingInit) {
    return (
      <div className="scrap-loading">
        <div className="scrap-loading-card">
          <div className="scrap-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="scrap-page">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

      <div className="scrap-shell">
        <div className="scrap-topbar">
          <div className="scrap-topbar-left">
            <div className="scrap-title-main">{view === "list" ? "Scrap Entry Explorer" : "Scrap Entry"}</div>
          </div>
          <div className="scrap-topbar-center">
            {view !== "list" && (
              <span className="scrap-topbar-text">{editId ? "Edit Record" : "New Entry"}</span>
            )}
          </div>
          <div className="scrap-topbar-right">
            <span className="scrap-topbar-text">{currentDateTime}</span>
          </div>
        </div>

        {view === "list" && (
          <div className="scrap-content scrap-explorer-content">
            <div className="scrap-panel scrap-explorer-panel">
              <div className="scrap-toolbar">
                <div className="scrap-toolbar-left">
                  <div className="scrap-toolgroup">
                    <button onClick={handleNew} className="scrap-tool" title="New Entry">
                      <span className="scrap-tool-icon">✎</span>
                      <span>New</span>
                    </button>
                    <button
                      onClick={() => selectedRow && handleEdit(selectedRow)}
                      disabled={!selectedRow || editLoading === selectedRow}
                      className="scrap-tool"
                      title="Edit"
                    >
                      <span className="scrap-tool-icon">{editLoading === selectedRow ? "…" : "E"}</span>
                      <span>Edit</span>
                    </button>
                    <button onClick={() => navigate(-1)} className="scrap-tool" title="Back">
                      <span className="scrap-tool-icon">↩</span>
                      <span>Back</span>
                    </button>
                  </div>
                </div>

                <div className="scrap-toolbar-right">
                  <input
                    type="text"
                    placeholder="Type to Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="scrap-search-inline"
                  />
                  <button type="button" className="scrap-search-icon" aria-label="Search">
                    🔍
                  </button>
                </div>
              </div>

              <div className="scrap-body">
                {loadingRecords ? (
                  <div className="scrap-loading-records">
                    <div>
                      <div className="scrap-spinner" style={{ margin: "0 auto 10px" }}></div>
                      <p>Loading records...</p>
                    </div>
                  </div>
                ) : filteredRecords.length === 0 ? (
                  <div className="scrap-empty">
                    <div>
                      <p>No records found.</p>
                      <button onClick={handleNew} className="scrap-navbtn" style={{ marginTop: "10px" }}>
                        Create New
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="scrap-table-wrap">
                    <table className="scrap-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Product Type</th>
                          <th>Moved From</th>
                          <th>BOE No</th>
                          <th>Inv No</th>
                          <th>Inv Date</th>
                          <th>Month</th>
                          <th className="scrap-num">Qty</th>
                          <th className="scrap-num">Net Wt</th>
                          <th className="scrap-num">Scrap Qty</th>
                          <th className="scrap-num">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRecords.map((row, i) => {
                          const primeId = row.Id ?? row.PrimeId ?? row.id;
                          const isSelected = selectedRow === primeId;
                          return (
                            <tr
                              key={i}
                              onClick={() => setSelectedRow(isSelected ? null : primeId)}
                              className={isSelected ? "scrap-row-selected" : ""}
                            >
                              <td>{i + 1}</td>
                              <td>{row.Product_type ?? "-"}</td>
                              <td>{row.Moved_from ?? "-"}</td>
                              <td className="scrap-linkish">{row.Boe_no ?? "-"}</td>
                              <td>{row.Inv_no ?? "-"}</td>
                              <td>{formatDateDDMMYYYY(row.Inv_date)}</td>
                              <td>{row.Month ?? "-"}</td>
                              <td className="scrap-num">{row.Qty != null ? Number(row.Qty).toLocaleString() : "-"}</td>
                              <td className="scrap-num">{row.Net_wt != null ? Number(row.Net_wt).toLocaleString() : "-"}</td>
                              <td className="scrap-num">{row.Scrap_qty != null ? Number(row.Scrap_qty).toLocaleString() : "-"}</td>
                              <td className="scrap-num scrap-linkish">{row.Value != null ? Number(row.Value).toLocaleString() : "-"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {filteredRecords.length > 0 && (
                  <div className="scrap-footer">
                    <span>Showing {filteredRecords.length} record{filteredRecords.length !== 1 ? "s" : ""}</span>
                    <span>
                      Total: {filteredRecords.reduce((sum, r) => sum + (Number(r.Value) || 0), 0).toLocaleString("en-IN", { style: "currency", currency: "INR" })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {view === "form" && (
          <div className="scrap-content">
            <div className="scrap-panel scrap-form-panel">
              <div className="scrap-panel-header">
                <div>
                  <div className="scrap-panel-title">{editId ? "Edit Scrap Record" : "New Scrap Entry"}</div>
                  <div className="scrap-panel-subtitle">{editId ? `Editing Record ID: ${editId}` : "Fill in the details below"}</div>
                </div>
                <div className="scrap-controls">
                  <button onClick={handleCancel} className="scrap-navbtn">
                    Back
                  </button>
                </div>
              </div>

              <div className="scrap-body">
                <div className="scrap-form-layout">
                  <div className="scrap-field">
                    <Label required>Product Type</Label>
                    <Select
                      options={dropdowns.productTypes}
                      value={form.Product_type}
                      onChange={(opt) => handleFieldChange("Product_type", opt)}
                      placeholder="Select product type..."
                      styles={selectStyles}
                      classNamePrefix="scrap-select"
                      isClearable
                    />
                  </div>

                  <div className="scrap-field">
                    <Label required>Moved From</Label>
                    <Select
                      options={dropdowns.movedFroms}
                      value={form.Moved_from}
                      onChange={(opt) => handleFieldChange("Moved_from", opt)}
                      placeholder="Select location..."
                      styles={selectStyles}
                      classNamePrefix="scrap-select"
                      isClearable
                    />
                  </div>

                  <div className="scrap-field">
                    <Label>BOE No</Label>
                    <Select
                      options={dropdowns.boeNos}
                      value={form.Boe_no}
                      onChange={(opt) => handleFieldChange("Boe_no", opt)}
                      placeholder="Select BOE..."
                      styles={selectStyles}
                      classNamePrefix="scrap-select"
                      isClearable
                    />
                  </div>

                  <div className="scrap-field">
                    <Label required>Invoice No</Label>
                    <TextInput
                      value={form.Inv_no}
                      onChange={(e) => handleFieldChange("Inv_no", e.target.value)}
                      placeholder="Enter invoice number"
                    />
                  </div>

                  <div className="scrap-field">
                    <Label required>Invoice Date</Label>
                    <DateInput
                      value={form.Inv_date}
                      onChange={(e) => handleFieldChange("Inv_date", e.target.value)}
                    />
                  </div>

                  <div className="scrap-field">
                    <Label>
                      Month
                      <span className="scrap-field-note">(auto-calculated)</span>
                    </Label>
                    <TextInput
                      value={form.Month}
                      readOnly
                      placeholder="e.g. 2022_09_Sep"
                      className="scrap-readonly"
                    />
                  </div>

                  <div className="scrap-field">
                    <Label>Quantity</Label>
                    <NumberInput
                      value={form.Qty}
                      onChange={(e) => handleFieldChange("Qty", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="scrap-field">
                    <Label>Net Weight</Label>
                    <NumberInput
                      value={form.Net_wt}
                      onChange={(e) => handleFieldChange("Net_wt", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="scrap-field">
                    <Label>Scrap Quantity</Label>
                    <NumberInput
                      value={form.Scrap_qty}
                      onChange={(e) => handleFieldChange("Scrap_qty", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="scrap-field">
                    <Label>Tr6 Challan No</Label>
                    <TextInput
                      value={form.Tr6_challan_no}
                      onChange={(e) => handleFieldChange("Tr6_challan_no", e.target.value)}
                      placeholder="Enter challan number"
                    />
                  </div>

                  <div className="scrap-field">
                    <Label>Tr6 Challan Date</Label>
                    <DateInput
                      value={form.Tr6_challan_date}
                      onChange={(e) => handleFieldChange("Tr6_challan_date", e.target.value)}
                    />
                  </div>

                  <div className="scrap-field">
                    <Label>Duty Paid (Rs)</Label>
                    <NumberInput
                      value={form.Duty_paid}
                      onChange={(e) => handleFieldChange("Duty_paid", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="scrap-field">
                    <Label>Quantity in Kgs</Label>
                    <NumberInput
                      value={form.Quantity_in_kgs}
                      onChange={(e) => handleFieldChange("Quantity_in_kgs", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="scrap-field">
                    <Label>Value (Rs)</Label>
                    <NumberInput
                      value={form.Value}
                      onChange={(e) => handleFieldChange("Value", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="scrap-field">
                    <Label>Remaining IGST Available (Rs)</Label>
                    <NumberInput
                      value={form.Remaining_igst_available}
                      onChange={(e) => handleFieldChange("Remaining_igst_available", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              <div className="scrap-form-actions">
                <button onClick={handleSave} disabled={saving} className="scrap-navbtn">
                  {saving ? "Saving..." : editId ? "Update" : "Save"}
                </button>
                <button onClick={handleCancel} disabled={saving} className="scrap-navbtn">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScrapEntry;
